import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2, FileText, Download, Loader2, Inbox, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

export function openBlankForm() {
  window.open(`${API}/forms/blank-pdf`, "_blank", "noopener");
}

export function openBlankFormDocx() {
  window.open(`${API}/forms/blank-docx`, "_blank", "noopener");
}
import { money } from "@/lib/tax";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export function downloadFilingPdf(id, confirmation, setBusy) {
  setBusy && setBusy(id);
  return api
    .get(`/filings/${id}/pdf`, { responseType: "blob" })
    .then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cheltenham-Tax-Filing-${confirmation || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    })
    .catch(() => toast.error("Could not download PDF"))
    .finally(() => setBusy && setBusy(null));
}

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api
      .get("/filings")
      .then((res) => setFilings(res.data))
      .catch(() => toast.error("Failed to load filings"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900">
              My Filings
            </h1>
            <p className="mt-2 text-slate-500">
              Mercantile &amp; Business Privilege Tax Returns you've submitted.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openBlankForm} variant="outline" data-testid="download-blank-form-btn" className="border-slate-300">
              <Printer className="w-4 h-4 mr-2" /> Form (PDF)
            </Button>
            <Button onClick={openBlankFormDocx} variant="outline" data-testid="download-blank-docx-btn" className="border-slate-300">
              <FileText className="w-4 h-4 mr-2" /> Form (Word)
            </Button>
            <Button
              onClick={() => navigate("/file")}
              data-testid="new-filing-btn"
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
            >
              <FilePlus2 className="w-4 h-4 mr-2" /> New Filing
            </Button>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          ) : filings.length === 0 ? (
            <div
              className="border border-dashed border-slate-300 rounded-md p-16 text-center bg-white"
              data-testid="empty-filings"
            >
              <span className="w-14 h-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-4">
                <Inbox className="w-7 h-7 text-slate-400" />
              </span>
              <h3 className="font-heading text-xl font-medium text-slate-800">No filings yet</h3>
              <p className="mt-2 text-slate-500">Start your first tax return to see it here.</p>
              <div className="mt-6 flex items-center justify-center gap-2">
                <Button
                  onClick={() => navigate("/file")}
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                  data-testid="empty-new-filing-btn"
                >
                  <FilePlus2 className="w-4 h-4 mr-2" /> Start a Filing
                </Button>
                <Button
                  onClick={openBlankForm}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="empty-download-blank-form-btn"
                >
                  <Printer className="w-4 h-4 mr-2" /> Form (PDF)
                </Button>
                <Button
                  onClick={openBlankFormDocx}
                  variant="outline"
                  className="border-slate-300"
                  data-testid="empty-download-blank-docx-btn"
                >
                  <FileText className="w-4 h-4 mr-2" /> Form (Word)
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid gap-4" data-testid="filings-list">
              {filings.map((f) => (
                <div
                  key={f.id}
                  className="bg-white border border-slate-200 rounded-md p-6 flex flex-wrap items-center justify-between gap-4 hover:shadow-sm transition-colors"
                  data-testid={`filing-row-${f.id}`}
                >
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 rounded-md bg-blue-50 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-[#1E3A8A]" />
                    </span>
                    <div>
                      <p className="font-medium text-slate-900">{f.business_name}</p>
                      <p className="text-sm text-slate-500 font-mono-data">
                        {f.confirmation_number}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Filed {new Date(f.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">Total Due</p>
                      <p className="font-mono-data text-lg font-semibold text-slate-900">
                        {money(f.totals?.l14)}
                      </p>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                      {f.status}
                    </Badge>
                    {f.totals?.l14 > 0 && (
                      <Badge
                        data-testid={`payment-badge-${f.id}`}
                        className={
                          f.payment_status === "paid"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                            : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50"
                        }
                      >
                        {f.payment_status === "paid" ? "Paid" : "Balance Due"}
                      </Badge>
                    )}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/filing/${f.id}`)}
                        data-testid={`view-filing-${f.id}`}
                        className="border-slate-300"
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        disabled={busy === f.id}
                        onClick={() => downloadFilingPdf(f.id, f.confirmation_number, setBusy)}
                        data-testid={`download-filing-${f.id}`}
                        className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                      >
                        {busy === f.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
